<template>
  <div class="backdrop">
    <transition name="fade">
      <div
        v-if="pageIndex === 0"
        class="onboarding-modal"
      >
        <div class="modal-title">
          Welcome to Weavr!
        </div>
        <div class="modal-subtitle">
          Let's make your experience with us even better
        </div>
        <div class="input-row">
          <label
            for="first-name-input"
            class="input-label"
          >
            First Name
          </label>
          <input
            id="first-name-input"
            v-model="firstName"
            type="text"
            placeholder="Jane"
            autocomplete="off"
            autocorrect="off"
            spellcheck="false"
            tabindex="1"
            autofocus
            @keydown.enter.prevent="submitNames"
          >
          <span
            :class="[{ invalid: firstName.length > firstNameCharLimit }, 'char-limit']"
          >
            {{ firstName.length }} / {{ firstNameCharLimit }} characters
          </span>
        </div>
        <div class="input-row">
          <label
            for="company-name-input"
            class="input-label"
          >
            Company Name
          </label>
          <input
            id="company-name-input"
            v-model="companyName"
            type="text"
            placeholder="Acme Studios"
            autocomplete="off"
            autocorrect="off"
            spellcheck="false"
            tabindex="2"
            @keydown.enter.prevent="submitNames"
          >
          <span
            :class="[{ invalid: companyName.length > companyNameCharLimit }, 'char-limit']"
          >
            {{ companyName.length }} / {{ companyNameCharLimit }} characters
          </span>
        </div>
        <div class="submit-container">
          <SpinnerButton
            class="submit-button"
            :isProcessing="isSubmittingNames"
            :isDisabled="!canSubmit"
            @enter="submitNames"
            @click="submitNames"
            tabindex="3"
          >
            Next
          </SpinnerButton>
        </div>
        <div class="progress-dot-container">
          <font-awesome-icon class="progress-dot" :icon="['fas', 'circle']" />
          <font-awesome-icon class="progress-dot" :icon="['far', 'circle']" />
          <font-awesome-icon class="progress-dot" :icon="['far', 'circle']" />
        </div>
      </div>
    </transition>
    <transition name="fade">
      <div
        v-if="pageIndex === 1"
        class="onboarding-modal"
      >
        <div class="modal-title">
          Upload a Profile Picture
        </div>
        <div class="modal-subtitle">
          Let's personalize your account
        </div>
        <div class="picture-component-container">
          <div class="profile-picture-container">
            <div class="profile-picture">
              <img
                ref="profileImage"
                :src="defaultProfilePicture"
              >
            </div>
          </div>
          <div
            v-if="!showImageActionButtons"
            class="upload-button-row"
          >
            <label
              for="profile-picture-input"
              class="submit-button"
            >
              Upload a photo
            </label>
            <input
              type="file"
              id="profile-picture-input"
              @change="uploadImage"
              :disabled="isSavingDefaultImage"
              accept="image/jpeg, image/png, image/gif"
            >
          </div>
          <div
            v-if="showImageActionButtons"
            class="upload-button-row"
          >
            <SpinnerButton
              class="submit-button"
              :isProcessing="isSavingUserImage"
              @click="saveUserImage"
            >
              Next
            </SpinnerButton>
            <SpinnerButton
              class="submit-button cancel"
              :isDisabled="isSavingUserImage"
              @click="clearImage"
            >
              Cancel
            </SpinnerButton>
          </div>
          <div class="upload-button-row">
            <SpinnerButton
              class="submit-button skip"
              :isProcessing="isSavingDefaultImage"
              :isDisabled="isSavingUserImage"
              @click="saveDefaultImage"
            >
              Skip for now
            </SpinnerButton>
          </div>
        </div>
        <div class="progress-dot-container">
          <font-awesome-icon class="progress-dot" :icon="['far', 'circle']" />
          <font-awesome-icon class="progress-dot" :icon="['fas', 'circle']" />
          <font-awesome-icon class="progress-dot" :icon="['far', 'circle']" />
        </div>
      </div>
    </transition>
    <transition name="fade">
      <div
        v-if="pageIndex === 2"
        class="onboarding-modal"
      >
        <div class="modal-title">
          You're All Set!
        </div>
        <div class="modal-subtitle">
          You can update your info anytime via the "Profile" tab.
        </div>
        <div class="modal-subtitle">
          Now let's get back to Weavr!
        </div>
        <div class="submit-container">
          <SpinnerButton
            class="submit-button"
            @click="leavePage"
          >
            Let's go!
          </SpinnerButton>
        </div>
        <div class="progress-dot-container">
          <font-awesome-icon class="progress-dot" :icon="['far', 'circle']" />
          <font-awesome-icon class="progress-dot" :icon="['far', 'circle']" />
          <font-awesome-icon class="progress-dot" :icon="['fas', 'circle']" />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import Vue from 'vue';
import SpinnerButton from '@/components/utilComponents/SpinnerButton';
const defaultProfilePicture = require('@/assets/default-profile-picture.jpg');
import compressImage from '@/utils/compressImage';

export default Vue.extend({
  name: 'OnboardingScreen',
  components: {
    SpinnerButton
  },
  props: {
    exitRoutePath: String
  },
  data: () => ({
    pageIndex: 0,
    firstName: '',
    companyName: '',
    firstNameCharLimit: 16,
    companyNameCharLimit: 48,
    defaultProfilePicture,
    fileToUpload: null,
    showImageActionButtons: false,
    isSubmittingNames: false,
    isSavingUserImage: false,
    isSavingDefaultImage: false,
    defaultExitRouteName: 'home',
    token: null
  }),
  computed: {
    auth0UserId: function() {
      return this.$auth.user.sub.replace(/\|/g, '_');
    },
    idToken: function () {
      return this.$auth.idToken;
    },
    canSubmit: function() {
      return this.firstName.length > 0 && this.firstName.length <= this.firstNameCharLimit &&
             this.companyName.length > 0 && this.companyName.length <= this.companyNameCharLimit;
    }
  },
  methods: {
    advancePage(currPageIndex) {
      if (currPageIndex === this.pageIndex) {
        // this check is to prevent people from double clicking the next/skip button on one page and skipping to the end.
        this.pageIndex += 1;
      }
    },
    async submitNames() {
      if (this.canSubmit) {
        this.isSubmittingNames = true;
        try {
          await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/onboarduser`, {
            auth0UserId: this.auth0UserId,
            firstName: this.firstName,
            companyName: this.companyName
          }, {
            headers: {
              'Content-Type': 'application/json', 
              'Authorization': `Bearer ${this.idToken}`
            }
          });
        } catch (error) {
          console.log('Error:', error);
        }
        this.isSubmittingNames = false;
        this.advancePage(0);
      }
    },
    async uploadImage(event) {
      const {file, src} = await compressImage(event.target.files[0]);
      this.fileToUpload = file;
      this.$refs.profileImage.src = src;
      this.showImageActionButtons = true;
    },
    async saveImageToCloud() {
      try {
        if (this.fileToUpload) {
          const response = await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/getprofilepictureuploadlink`, {
            auth0UserId: this.auth0UserId,
            imageType: this.fileToUpload.type
          }, {
            headers: {
              'Content-Type': 'application/json', 
              'Authorization': `Bearer ${this.idToken}`
            }
          });
          // NOTE: Careful, use axios.put here. DO NOT USE axios.post because you won't match the AWS signature!
          await this.axios.put(
            response.data.uploadLink,
            this.fileToUpload,
            { 
              headers: {
                'Content-Type': this.fileToUpload.type
              }
            }
          );
        }
      } catch (error) {
        console.log('Error:', error);
        console.log('error.body:', error.body);
      }
    },
    async saveDefaultImage() {
      this.isSavingDefaultImage = true;
      try {
        const response = await fetch(this.defaultProfilePicture);
        const data = await response.blob();
        const metadata = {
          type: 'image/jpeg'
        };
        this.fileToUpload = new File([data], "default-profile-picture.jpg", metadata);
      } catch (error) {
        console.log('Error:', error);
      }
      await this.saveImageToCloud();
      this.isSavingDefaultImage = false;
      this.advancePage(1);
    },
    async saveUserImage() {
      this.isSavingUserImage = true;
      await this.saveImageToCloud();
      this.isSavingUserImage = false;
      this.advancePage(1);
    },
    clearImage() {
      this.$refs.profileImage.src = this.defaultProfilePicture;
      this.showImageActionButtons = false;
    },
    leavePage() {
      if (this.exitRoutePath) {
        this.$router.push({
          path: this.exitRoutePath
        });
      } else {
        this.$router.push({
          name: this.defaultExitRouteName
        });
      }
    },
    async verifyJWT() {
      try {
        const response = await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/verifyjwt`, {
          token: this.token
        }, {
          headers: {
            'Content-Type': 'application/json', 
            'Authorization': `Bearer ${this.idToken}`
          }
        });
        this.companyName = response.data.decoded.companyName;
      } catch (error) {
        console.log('Error:', error);
      }
    },
  },
  created: async function() {
    if (this.exitRoutePath && this.exitRoutePath.includes('?token=')) {
      this.token = this.exitRoutePath.split('?token=')[1];
      if (this.token) {
        await this.verifyJWT();
      }
    }
  }
})
</script>

<style scoped lang="scss">
.backdrop {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  background-color: $natural-white;
}

.onboarding-modal {
  display: flex;
  flex-direction: column;
  position: absolute;
  text-align: center;
  top: 4%;
  left: 30%;
  right: 30%;
  max-height: 100%;
  max-width: 100%;
  min-height: max-content;
  min-width: max-content;
  border: 0.1em solid $brand-teal;
  border-radius: 0.4em;
  background-color: white;
  box-shadow: 0 6px 8px -4px $baby-blue;
  transition: opacity 600ms ease-in;
}

.modal-title {
  flex: 0;
  font-size: 1.8em;
  height: max-content;
  padding: 0.8em 0 0.6em;
  border-radius: 0.2em;
  color: $brand-teal;
}

.modal-subtitle {
  flex: 0;
  height: max-content;
  padding: 0.1em 0;
  color: $dim-gray;
}

.input-row {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 0.2em 0.8em;
}

.input-label {
  font-size: 1.2em;
  padding: 0.4em 0.8em;
  text-align: left;
}

#first-name-input, #company-name-input {
  font-size: 1em;
  width: 95%;
  padding: 0.4em 0.8em;
  margin: 0 auto;
  font-family: inherit;
}

.char-limit {
  font-size: 0.8em;
  padding: 0.4em 1.2em;
  text-align: left;
  &.invalid {
    color: $crimson;
  }
}

.submit-container {
  flex: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 1em 0;
  min-height: max-content;
}

.submit-button {
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  margin: 0.3em 0.2em;
  padding: 0.6em 1em;
  font-size: 1.2em;
  border-radius: 0.4em;
  box-shadow: 0 1px 1px 1px $light-gray;
  color: $natural-white;
  background-color: $brand-teal;
  height: 2.8em;
  width: 8em;
  min-width: max-content;
  min-height: max-content;
  user-select: none;
  cursor: pointer;
  &:hover {
    background-color: $light-teal;
  }
  &.disabled {
    background-color: $light-gray;
    box-shadow: 0 1px 1px 1px $bright-gray;
    pointer-events: none;
  }
  &.skip {
    font-size: 0.9em;
    background-color: $lilac-purple;
  }
  &.skip:hover {
    font-size: 0.9em;
    background-color: $light-lilac-purple;
  }
  &.cancel {
    background-color: $crimson;
  }

  &.cancel:hover {
    background-color: $light-crimson;
  }
}

.progress-dot-container {
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 0.8em;
  margin-top: 0.4em;
  margin-bottom: 1.4em;
}

.progress-dot {
  color: $brand-teal;
  margin: 0 0.4em;
}

.picture-component-container {
  height: 24em;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.profile-picture-container {
  display: flex;
  justify-content: center;
  flex: 1;
}

.profile-picture {
  height: 12em;
  width: 12em;
  border-radius: 50%;
  overflow: hidden;
  & > img {
    height: 100%;
    width: 100%;
    object-fit: cover;
  }
}

#profile-picture-input {
  position: absolute;
  opacity: 0;
  height: 0;
}

.upload-button-row {
  display: flex;
  justify-content: center;
  & + .upload-button-row {
    margin-bottom: 1.2em;
  }
}

.fade-enter-active {
  transition: opacity 300ms ease-in-out;
}

.fade-leave-active {
  transition: opacity 300ms ease-in-out;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>
